import { emperor } from '~/services';
import { UserMin } from '~/types/user';

export type ListingType = 'sale' | 'free' | 'iso';

export type ListingCategory =
  | 'furniture'
  | 'electronics'
  | 'clothing'
  | 'home_goods'
  | 'toys_kids'
  | 'books'
  | 'sports'
  | 'plants'
  | 'other';

export type ListingStatus = 'active' | 'sold' | 'gone' | 'found' | 'removed';

// The staff view of a listing (`GET /building/:id/listing`): the resident shape
// plus moderation state, every photo (withheld ones flagged), and a report count.
export type Listing = {
  uuid: string;
  seller: {
    uuid: string;
    name: string;
    avatar: string | null;
    memberSince: string;
    closedCount: number;
  };
  listingType: ListingType;
  category: ListingCategory;
  condition: 'like_new' | 'good' | 'fair' | null;
  title: string;
  description: string | null;
  /** Integer cents. 0 = free, null = ISO. */
  priceCents: number | null;
  previousPriceCents: number | null;
  status: ListingStatus;
  closedAt: string | null;
  viewCount: number;
  commentCount: number;
  images: { uuid: string; url: string; hidden: boolean }[];
  createdAt: string;
  updatedAt: string;
  hidden: boolean;
  hiddenBy: string | null;
  passedmanualmoderation: boolean;
  reportCount: number;
};

export type ListingFilters = {
  status?: ListingStatus;
  listingType?: ListingType;
  category?: ListingCategory;
  hidden?: boolean;
};

export type ListingsPage = {
  listings: Listing[];
  page: number;
  limit: number;
  totalPages: number;
  /** Totals for the whole filtered set, not just this page. */
  counts: { total: number; hidden: number; reported: number };
};

export const LISTINGS_PAGE_SIZE = 25;

export const fetchListings = async (
  buildingId: string,
  filters: ListingFilters = {},
  page = 1
): Promise<ListingsPage> => {
  const res = await emperor.get(`/building/${buildingId}/listing`, {
    params: { ...filters, page, limit: LISTINGS_PAGE_SIZE }
  });
  return res.data;
};

export const updateListing = async (
  listingId: string,
  patch: { hidden?: boolean; status?: ListingStatus }
): Promise<Listing> => {
  const res = await emperor.patch(`/admin/listing/${listingId}`, patch);
  return res.data;
};

export type ListingReport = {
  uuid: string;
  listing: string;
  reason: string;
  createdAt: string;
  resolvedAt: string | null;
  reportedBy: {
    uuid: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
};

export const fetchListingReports = async (
  listingId: string
): Promise<ListingReport[]> => {
  const res = await emperor.get(`/admin/listing/${listingId}/report`);
  return res.data;
};

export const resolveListingReport = async (
  reportId: string,
  resolved: boolean
): Promise<ListingReport> => {
  const res = await emperor.patch(`/admin/listing-report/${reportId}`, {
    resolved
  });
  return res.data;
};

export type ListingComment = {
  uuid: string;
  listing: string;
  listingTitle: string;
  listingCategory: ListingCategory;
  parent: string | null;
  /** Empty for a photo-only comment. */
  contents: string;
  /** Released photos only; withheld ones are reviewed in Image Moderation. */
  images: { uuid: string; image: string }[];
  hidden: boolean;
  hiddenBy?: string;
  user: UserMin;
  authorName: string;
  createdAt: string;
  myReaction?: string;
  reactionsCount?: number;
  byReactionUserIds?: Record<string, string[]>;
};

export type ListingCommentsResponse = {
  comments: ListingComment[];
  countsByCategory: Partial<Record<ListingCategory, number>>;
  /** Set when a category is requested. */
  hasMore?: boolean;
};

// Pass `category` to page through that category's comments; omit it to fetch
// only `countsByCategory` (cheap — used for the MARKETPLACE sidebar badges).
export const fetchListingComments = async (
  buildingId: string,
  category?: ListingCategory,
  page = 1
): Promise<ListingCommentsResponse> => {
  const res = await emperor.get(`/building/${buildingId}/listing-comment`, {
    params: category ? { category, page } : {}
  });
  return res.data;
};

export const updateListingComment = async (
  commentId: string,
  hidden: boolean
): Promise<ListingComment> => {
  const res = await emperor.patch(`/listing-comment/${commentId}`, { hidden });
  return res.data;
};

export const reactToListingComment = async (
  commentId: string,
  reactionName: string
): Promise<{ reactionsCount: number; myReaction: string | null }> => {
  const res = await emperor.post(`/listing-comment/${commentId}/react`, {
    reactionName
  });
  return res.data;
};

export const unreactToListingComment = async (
  commentId: string
): Promise<{ reactionsCount: number; myReaction: string | null }> => {
  const res = await emperor.delete(`/listing-comment/${commentId}/react`);
  return res.data;
};
